import axios from 'axios';
import { isEmbedded, getSessionToken } from './appBridge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Embedded (Shopify admin) mode: attach an App Bridge session token so the
// backend can verify the caller via JWT (Built-for-Shopify session auth).
api.interceptors.request.use(async (config) => {
  if (isEmbedded()) {
    const token = await getSessionToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Silence expected background-fetch noise from single-tenant mode:
//   • 401 → unauth (we surface auth state via a dedicated flow, not console)
//   • 404 → optional/legacy endpoints
//   • 422 → query-param validation when a page passes no timeframe
// All other errors propagate as usual.
const SILENT_STATUSES = new Set([401, 404, 422]);
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    if (SILENT_STATUSES.has(status)) {
      err.__silent = true;
    }
    return Promise.reject(err);
  }
);

// Keep console clean from these expected statuses by short-circuiting
// the default "unhandled rejection" path when a caller doesn't catch.
if (typeof window !== "undefined" && !window.__ago_axios_handler__) {
  window.__ago_axios_handler__ = true;
  window.addEventListener("unhandledrejection", (ev) => {
    if (ev?.reason?.__silent) ev.preventDefault();
  });
}

// Auth
export const exchangeSession = (sessionId) => api.post('/auth/session', { session_id: sessionId });
export const getCurrentUser = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');
export const shopifyAutoLogin = () => api.post('/auth/shopify-auto-login');

// Dashboard
export const getDashboardMetrics = (timeframe) => {
  const params = {};
  if (timeframe) params.timeframe = timeframe;
  return api.get('/dashboard/metrics', { params });
};

// Products
export const getProducts = (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get('/products', { params });
};
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// Price Changes
export const getPriceChanges = (status) => api.get('/price-changes', { params: { status } });
export const createPriceChange = (data) => api.post('/price-changes', data);
export const applyPriceChange = (id) => api.put(`/price-changes/${id}/apply`);
export const rejectPriceChange = (id) => api.put(`/price-changes/${id}/reject`);

// Orders
export const getOrders = (limit = 100, startDate, endDate) => {
  const params = { limit };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get('/orders', { params });
};
export const getOrder = (id) => api.get(`/orders/${id}`);

// Customers
export const getCustomers = (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get('/customers', { params });
};
export const getCustomer = (id) => api.get(`/customers/${id}`);

// Campaigns
export const getCampaigns = (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get('/campaigns', { params });
};
export const getCampaign = (id) => api.get(`/campaigns/${id}`);
export const createCampaign = (data) => api.post('/campaigns', data);

// AI Recommendations
export const getRecommendations = (status) => api.get('/recommendations', { params: { status } });
export const generateRecommendations = () => api.post('/recommendations/generate');
export const dismissRecommendation = (id) => api.put(`/recommendations/${id}/dismiss`);
export const applyRecommendation = (id) => api.put(`/recommendations/${id}/apply`);

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);

// Demo Data
export const seedDemoData = () => api.post('/seed-demo-data');

// Email Reports
export const updateEmailReportSettings = (data) => api.post('/reports/email/settings', data);
export const sendTestReport = () => api.post('/reports/email/send-test');

// Export
export const exportProducts = (format = 'csv', startDate, endDate) => {
  const params = { format };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return `${API}/export/products?${new URLSearchParams(params)}`;
};
export const exportOrders = (format = 'csv', startDate, endDate) => {
  const params = { format };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return `${API}/export/orders?${new URLSearchParams(params)}`;
};
export const exportCustomers = (format = 'csv') => `${API}/export/customers?format=${format}`;
export const exportCampaigns = (format = 'csv') => `${API}/export/campaigns?format=${format}`;
export const exportFullReport = (format = 'json') => `${API}/export/report?format=${format}`;

// Shopify
export const connectShopify = (storeDomain, accessToken) => 
  api.post('/shopify/connect', { store_domain: storeDomain, access_token: accessToken });
export const syncShopify = () => api.post('/shopify/sync');
export const updateShopifyPrice = (productId, newPrice) => 
  api.post('/shopify/update-price', { product_id: productId, new_price: newPrice });

// Bundling AI
export const getBundleSuggestions = () => api.get('/bundling/suggestions');
export const getTripletBundles = () => api.get('/bundling/triplets');
export const createBundle = (data) => api.post('/bundling/create', data);
export const getActiveBundles = () => api.get('/bundling/active');

// Return Prediction
export const predictReturn = (data) => api.post('/returns/predict', data);
export const analyzeOrdersReturnRisk = () => api.get('/returns/analyze-orders');
export const getHighRiskOrders = () => api.get('/returns/high-risk');
export const recordCustomerReturn = (customerId, data) => api.post(`/customers/${customerId}/return`, data);

// Inventory Forecasting
export const getInventoryForecast = () => api.get('/inventory/forecast');
export const getInventoryAlerts = () => api.get('/inventory/alerts');

// CRO Automation
export const getCROMetrics = () => api.get('/cro/metrics');
export const getCROSuggestions = () => api.get('/cro/suggestions');
export const createABTest = (data) => api.post('/cro/ab-test', data);
export const getABTests = () => api.get('/cro/ab-tests');

// Pricing AI
export const getPricingAnalysis = () => api.get('/pricing/analysis');
export const getPricingSuggestions = () => api.get('/pricing/suggestions');
export const simulatePrice = (productId, newPrice) => api.post('/pricing/simulate', { product_id: productId, new_price: newPrice });
export const applyPriceSuggestion = (suggestionId, mode) => api.post(`/pricing/apply/${suggestionId}`, { mode });
export const bulkUpdatePrices = (data) => api.post('/pricing/bulk-update', data);

// Price A/B Testing
export const createPriceABTest = (data) => api.post('/pricing/ab-test', data);
export const getPriceABTests = (status) => api.get('/pricing/ab-tests', { params: { status } });
export const getPriceABTest = (testId) => api.get(`/pricing/ab-tests/${testId}`);
export const endPriceABTest = (testId, data) => api.post(`/pricing/ab-tests/${testId}/end`, data);
export const deletePriceABTest = (testId) => api.delete(`/pricing/ab-tests/${testId}`);

// Price History
export const getPriceHistory = (params) => api.get('/pricing/history', { params });
export const getPriceHistoryStats = () => api.get('/pricing/history/stats');

// Pricing Rules
export const createPricingRule = (data) => api.post('/pricing/rules', data);
export const getPricingRules = (status) => api.get('/pricing/rules', { params: { status } });
export const getPricingRule = (ruleId) => api.get(`/pricing/rules/${ruleId}`);
export const updatePricingRule = (ruleId, data) => api.put(`/pricing/rules/${ruleId}`, data);
export const deletePricingRule = (ruleId) => api.delete(`/pricing/rules/${ruleId}`);
export const togglePricingRule = (ruleId) => api.post(`/pricing/rules/${ruleId}/toggle`);
export const runAllRules = () => api.post('/pricing/rules/run');
export const runPricingRule = (ruleId) => api.post(`/pricing/rules/${ruleId}/run`);
export const getRuleExecutionLogs = (params) => api.get('/pricing/rules/logs', { params });
export const getRuleTemplates = () => api.get('/pricing/rules/templates');
export const getRulePerformance = () => api.get('/pricing/rules/performance');
export const getSingleRulePerformance = (ruleId) => api.get(`/pricing/rules/${ruleId}/performance`);

// Shopify Integration
export const getShopifyStatus = () => api.get('/shopify/status');
export const configureShopify = (data) => api.post('/shopify/configure', data);
export const syncShopifyProducts = () => api.post('/shopify/sync/products');
export const syncShopifyOrders = () => api.post('/shopify/sync/orders');
export const syncShopifyCustomers = () => api.post('/shopify/sync/customers');
export const syncAllShopifyData = () => api.post('/shopify/sync/all');
export const updateShopifyProductPrice = (productId, price) => api.put(`/shopify/products/${productId}/price`, { price });

// Scheduled Sync
export const getSchedulerStatus = () => api.get('/shopify/scheduler/status');
export const startScheduler = (interval) => api.post('/shopify/scheduler/start', { interval });
export const stopScheduler = () => api.post('/shopify/scheduler/stop');
export const getSyncLogs = (limit = 20) => api.get('/shopify/sync/logs', { params: { limit } });
export const getSyncIntervals = () => api.get('/shopify/sync/intervals');

// Customer Cohorts
export const getCustomerCohorts = (granularity = 'monthly') => api.get('/customers/cohorts', { params: { granularity } });

// Multi-Currency Support
export const getSupportedCurrencies = () => api.get('/currencies');
export const getCurrentCurrency = () => api.get('/currency/current');
export const updateCurrency = (currency) => api.put('/currency', { currency });
export const syncCurrencyFromShopify = () => api.post('/currency/sync-from-shopify');

// Email Provider Configuration
export const getEmailProviders = () => api.get('/email-providers');
export const getCurrentEmailProvider = () => api.get('/email-provider/current');
export const updateEmailProvider = (provider, config) => api.put('/email-provider', { provider, config });
export const testEmailProvider = () => api.post('/email-provider/test');

// Weekly Reports
export const generateWeeklyReport = () => api.post('/reports/weekly/generate');
export const sendWeeklyReport = () => api.post('/reports/weekly/send');

// Enhanced Bundling AI
export const getEnhancedBundles = () => api.get('/bundles/enhanced');

// AI Autopilot
export const getAutopilotSettings = () => api.get('/autopilot/settings');
export const updateAutopilotSettings = (data) => api.put('/autopilot/settings', data);
export const runAutopilot = () => api.post('/autopilot/run');
export const getAutopilotActions = (params) => api.get('/autopilot/actions', { params });
export const approveAutopilotAction = (actionId) => api.put(`/autopilot/actions/${actionId}/approve`);
export const rejectAutopilotAction = (actionId) => api.put(`/autopilot/actions/${actionId}/reject`);
export const rollbackAutopilotAction = (actionId) => api.post(`/autopilot/actions/${actionId}/rollback`);
export const getAutopilotRollbackHistory = (limit = 50) => api.get('/autopilot/rollback-history', { params: { limit } });
export const getAutopilotStats = () => api.get('/autopilot/stats');

// Analytics Integrations (GA4, Search Console, Meta Ads, Google Ads)
export const getAnalyticsIntegrations = () => api.get('/analytics/integrations');
export const connectAnalyticsPlatform = (platform, data) => api.post(`/analytics/connect/${platform}`, data);
export const disconnectAnalyticsPlatform = (platform) => api.delete(`/analytics/disconnect/${platform}`);
export const getGA4Data = (params) => api.get('/analytics/ga4/data', { params });
export const getSearchConsoleData = (params) => api.get('/analytics/search-console/data', { params });
export const getMetaAdsData = (params) => api.get('/analytics/meta-ads/data', { params });
export const getGoogleAdsData = (params) => api.get('/analytics/google-ads/data', { params });
export const getCombinedAnalyticsDashboard = () => api.get('/analytics/combined-dashboard');

// Vertical-Specific AI Models
export const getVerticals = () => api.get('/verticals');
export const getCurrentVertical = () => api.get('/verticals/current');
export const selectVertical = (verticalId) => api.put('/verticals/select', { vertical_id: verticalId });
export const getVerticalConfig = (verticalId) => api.get(`/verticals/${verticalId}/config`);
export const getVerticalRecommendations = () => api.get('/verticals/recommendations');

// Real-time Webhooks
export const registerWebhooks = () => api.post('/webhooks/register');
export const getWebhookStatus = () => api.get('/webhooks/status');

// COGS Tracking
export const getAllCogs = () => api.get('/cogs');
export const setProductCogs = (data) => api.post('/cogs', data);
export const bulkUpdateCogs = (items) => api.post('/cogs/bulk', { items });

// Marketing Attribution
export const getAttributionOverview = (params) => api.get('/attribution/overview', { params });
export const getCampaignAttribution = () => api.get('/attribution/campaigns');

// Automated Pricing
export const getAutomatedPricingStatus = () => api.get('/pricing/automated/status');
export const updateAutomatedPricingSettings = (data) => api.put('/pricing/automated/settings', data);
export const runAutomatedPricing = () => api.post('/pricing/automated/run');

// Bundle Opportunities
export const getBundleOpportunities = () => api.get('/bundles/opportunities');

// Alerts
export const getProfitAlerts = () => api.get('/alerts/profit');
export const getInventoryAlertsFromAlerts = () => api.get('/alerts/inventory');
export const getAllAlerts = () => api.get('/alerts/all');

// Customer Profitability
export const getCustomerProfitability = () => api.get('/customer-profitability');

// Real-time Metrics
export const getRealtimeMetrics = () => api.get('/metrics/realtime');

// Insights
export const getProfitLeaks = () => api.get('/insights/profit-leaks');
export const getGrowthOpportunities = () => api.get('/insights/growth-opportunities');

// ============ COMPREHENSIVE REPORTING SYSTEM ============

// Report Types
export const getReportTypes = () => api.get('/reports/types');

// Executive Summary
export const getExecutiveSummary = (period = 'last_30_days', customStart, customEnd) => {
  const params = { period };
  if (customStart) params.custom_start = customStart;
  if (customEnd) params.custom_end = customEnd;
  return api.get('/reports/executive-summary', { params });
};

// Profit & Loss Report
export const getProfitLossReport = (period = 'last_30_days', customStart, customEnd) => {
  const params = { period };
  if (customStart) params.custom_start = customStart;
  if (customEnd) params.custom_end = customEnd;
  return api.get('/reports/profit-loss', { params });
};

// Product Performance Report
export const getProductPerformanceReport = (period = 'last_30_days', customStart, customEnd, limit = 50) => {
  const params = { period, limit };
  if (customStart) params.custom_start = customStart;
  if (customEnd) params.custom_end = customEnd;
  return api.get('/reports/product-performance', { params });
};

// Customer LTV Report
export const getCustomerLTVReport = (period = 'last_90_days', customStart, customEnd) => {
  const params = { period };
  if (customStart) params.custom_start = customStart;
  if (customEnd) params.custom_end = customEnd;
  return api.get('/reports/customer-ltv', { params });
};

// Channel Attribution Report
export const getChannelAttributionReport = (period = 'last_30_days', customStart, customEnd) => {
  const params = { period };
  if (customStart) params.custom_start = customStart;
  if (customEnd) params.custom_end = customEnd;
  return api.get('/reports/channel-attribution', { params });
};

// Inventory Health Report
export const getInventoryHealthReport = () => api.get('/reports/inventory-health');

// AI Recommendations Report
export const getAIRecommendationsReport = (period = 'last_30_days') => 
  api.get('/reports/ai-recommendations', { params: { period } });

// Trend Analysis Report
export const getTrendAnalysisReport = (period = 'last_90_days') => 
  api.get('/reports/trend-analysis', { params: { period } });

// Report Schedules
export const getReportSchedules = () => api.get('/reports/schedules');
export const createReportSchedule = (data) => api.post('/reports/schedules', data);
export const deleteReportSchedule = (scheduleId) => api.delete(`/reports/schedules/${scheduleId}`);

// Custom Reports
export const getAvailableMetrics = () => api.get('/reports/custom/metrics');
export const getCustomReports = () => api.get('/reports/custom');
export const createCustomReport = (data) => api.post('/reports/custom', data);
export const generateCustomReport = (reportId, period = 'last_30_days') => 
  api.post(`/reports/custom/${reportId}/generate`, null, { params: { period } });

// Export Report
export const exportReport = (reportType, format = 'json', period = 'last_30_days') => 
  api.get(`/reports/export/${reportType}`, { params: { format, period } });

// Report Templates
export const getReportTemplates = () => api.get('/reports/templates');

// Autopilot Projections
export const getAutopilotProjections = () => api.get('/autopilot/projections');

// Creative AI
export const getCreativeSuggestions = () => api.get('/creative-ai/suggestions');
export const generateCreativeContent = (data) => api.post('/creative-ai/generate', data);
export const generateProductImage = (data) => api.post('/creative-ai/generate-image', data);
export const analyzeProductImage = (imageUrl) => api.post(`/creative-ai/analyze-image?image_url=${encodeURIComponent(imageUrl)}`);

// Creative AI Templates
export const getCreativeTemplates = () => api.get('/creative-ai/templates');
export const getCreativeTemplate = (templateId) => api.get(`/creative-ai/templates/${templateId}`);
export const generateFromTemplate = (data) => api.post('/creative-ai/templates/generate', data);

// Shopify Asset Improvement
export const scanShopifyAssets = () => api.get('/creative-ai/shopify-assets');
export const improveShopifyAsset = (data) => api.post('/creative-ai/improve-asset', data);
export const bulkImproveAssets = (data) => api.post('/creative-ai/bulk-improve', data);
export const getBulkImproveStatus = (jobId) => api.get(`/creative-ai/bulk-improve/${jobId}`);
export const pushToShopify = (data) => api.post('/creative-ai/push-to-shopify', data);
export const getContentUpdateHistory = (limit = 50) => api.get('/creative-ai/update-history', { params: { limit } });

// A/B Testing (Content Testing)
export const createContentABTest = (data) => api.post('/ab-tests/create', data);
export const getContentABTests = (status = null, limit = 50) => api.get('/ab-tests', { params: { status, limit } });
export const getContentABTest = (testId) => api.get(`/ab-tests/${testId}`);
export const trackContentABTestEvent = (data) => api.post('/ab-tests/track', data);
export const simulateContentABTestData = (testId, days = 7) => api.post(`/ab-tests/${testId}/simulate`, { days });
export const applyContentABTestWinner = (testId) => api.post(`/ab-tests/${testId}/apply-winner`);
export const stopContentABTest = (testId) => api.post(`/ab-tests/${testId}/stop`);
export const deleteContentABTest = (testId) => api.delete(`/ab-tests/${testId}`);
export const getContentABTestDashboardStats = () => api.get('/ab-tests/dashboard/stats');

// Competitor Analysis
export const getCompetitorAnalysis = () => api.get('/competitors/analysis');
export const getCompetitorKeywords = (competitorId) => api.get(`/competitors/${competitorId}/keywords`);
export const getCompetitorAdCopy = () => api.get('/competitors/ad-copy');

// Competitor Watch AI
export const getCompetitorWatchlist = () => api.get('/competitors/watch/list');
export const addToWatchlist = (data) => api.post('/competitors/watch/add', data);
export const removeFromWatchlist = (watchId) => api.delete(`/competitors/watch/${watchId}`);
export const updateWatchSettings = (watchId, data) => api.put(`/competitors/watch/${watchId}/settings`, data);
export const toggleWatch = (watchId) => api.post(`/competitors/watch/${watchId}/toggle`);
export const getWatchAlerts = (limit = 50, unreadOnly = false) => api.get('/competitors/watch/alerts', { params: { limit, unread_only: unreadOnly } });
export const markAlertRead = (alertId) => api.post(`/competitors/watch/alerts/${alertId}/read`);
export const markAllAlertsRead = () => api.post('/competitors/watch/alerts/read-all');
export const runCompetitorScan = () => api.post('/competitors/watch/scan');
export const getWatchDashboard = () => api.get('/competitors/watch/dashboard');

// External Platform Integrations (Google Search Console, Google Ads, Meta Ads)
export const getIntegrationsStatus = () => api.get('/integrations/status');
export const getGSCAuthUrl = () => api.get('/integrations/gsc/auth-url');
export const getGSCSites = () => api.get('/integrations/gsc/sites');
export const getGSCAnalytics = (siteUrl, days = 30) => api.get('/integrations/gsc/analytics', { params: { site_url: siteUrl, days } });
export const getGoogleAdsAuthUrl = () => api.get('/integrations/google-ads/auth-url');
export const getGoogleAdsCampaigns = (days = 30) => api.get('/integrations/google-ads/campaigns', { params: { days } });
export const getGoogleAdsKeywords = (campaignId, days = 30) => api.get('/integrations/google-ads/keywords', { params: { campaign_id: campaignId, days } });
export const getMetaAdsAuthUrl = () => api.get('/integrations/meta-ads/auth-url');
export const getMetaAdsCampaigns = (days = 30) => api.get('/integrations/meta-ads/campaigns', { params: { days } });
export const getMetaAdsAudiences = () => api.get('/integrations/meta-ads/audiences');
export const getCombinedCampaigns = (days = 30) => api.get('/integrations/combined/campaigns', { params: { days } });
export const disconnectIntegration = (platform) => api.delete(`/integrations/${platform}/disconnect`);

// Report Exports
export const downloadWeeklyReportPDF = () => {
  const baseURL = process.env.REACT_APP_BACKEND_URL || '';
  window.open(`${baseURL}/api/reports/weekly/pdf`, '_blank');
};

export default api;
